<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="autocomplete ds1-utd-font">
    <div :class="{ 'autocomplete-panel': shouldShowAskExpertAi }">
      <AskExpertAiSection v-if="shouldShowAskExpertAi"
                          :suggestions="searchSuggestionItems" />
      <ul id="search-listbox"
          class="autocomplete-ul"
          :class="{ 'autocomplete-ul--with-ask-expert': shouldShowAskExpertAi }"
          role="listbox"
          aria-labelledby="search-uptodate">
        <template v-for="item in list">
          <li v-if="item.isHeader"
              :id="item.elementId"
              :key="item.label"
              class="autocomplete-li"
              role="option"
              :aria-selected="false"
              :aria-disabled="true"
              :class="getItemClasses(item)">
            <span :class="item.subClasses"
                  v-text="item.label" />
          </li>
          <li v-else-if="item.isClickable"
              :id="item.elementId"
              :key="item.uniqueId"
              class="autocomplete-li needsclick ac-navable"
              :class="getItemClasses(item)"
              :alt-search-term="item.altSearchTerm"
              role="option"
              :aria-selected="isActive(item)"><!--
            --><span v-if="isPersonalized(item)" class="glyph">&#9679;</span><!--
            --><!-- eslint-disable vue/no-v-html -->
            <a v-utd-new-tab="item.isNewTab" 
               :href="item.href"
               :class="getNewTabClasses(item)" 
               @mousedown="onClicked($event, item)"
               @click="onClicked($event, item)"
               v-html="item.labelHTML || getBoldfacedLabel(item)"
            />
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import UtdNewTab from '_acaSrc/directives/UtdNewTab.directive';
import { mapGetters } from 'vuex';
import { C_AUTOCOMPLETE } from '_acaSrc/utility/constants';
import AskExpertAiSection from './AskExpertAiSection.vue';

export default {
    components: {
        AskExpertAiSection
    },
    directives: {
        UtdNewTab
    },
    props: {
        list: Array,
        activeIndex: Number,
        options: {
            type: Object,
            default() {
                return {};
            }
        },
        showAskExpertAi: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters('search', [
            'searchParams'
        ]),
        navableItems() {
            return this.list.filter(i => i.type === 'link' || i.type === 'personalized');
        },
        activeItemId() {
            if (!(Array.isArray(this.navableItems)
                && this.activeIndex > -1
                && this.activeIndex < this.navableItems.length)) {
                return -1;
            }
            return this.navableItems[this.activeIndex].uniqueId;
        },
        searchSuggestionItems() {
            const list = Array.isArray(this.list) ? this.list : [];
            const searchSuggestionsHeaderIdx = list.findIndex(
                item => item?.elementId === 'ac-search-suggestions-header'
            );
            if (searchSuggestionsHeaderIdx < 0) {
                return [];
            }

            const itemsAfterHeader = list.slice(searchSuggestionsHeaderIdx + 1);
            const nextHeaderIdx = itemsAfterHeader.findIndex(item => item?.isHeader);
            const searchSuggestionSection = nextHeaderIdx < 0
                ? itemsAfterHeader
                : itemsAfterHeader.slice(0, nextHeaderIdx);

            return searchSuggestionSection.filter(item => item?.isClickable && item?.label);
        },
        shouldShowAskExpertAi() {
            return this.showAskExpertAi && this.searchSuggestionItems.length > 0;
        }
    },
    methods: {
        onClicked(evt, item) {
            item.onClick && item.onClick(evt);
        },
        isActive(item) {
            return item.uniqueId === this.activeItemId;
        },
        findItemIndexByUniqueId(uniqueId) {
            return this.list?.findIndex(item => item?.uniqueId === uniqueId);
        },
        unboldSearchTextInLabel(searchText, label) {
            const replaceText = searchText.trim()
                .replace(/\\/g, '')
                .replace(/\[/g, '\\[')
                .replace(/\(/g, '\\(')
                .replace(/\)/g, '\\)')
                .replace(/\+/g, '\\+')
                .replace(/\*/g, '\\*');
            const replaceRegex = new RegExp(`(${replaceText})`, 'i');
            return label.replace(replaceRegex,
                '<span class="ds1-utd-weight-normal">$1</span>');
        },
        // eslint-disable-next-line complexity
        handleGuidedCompletionsBoldLabel(item, autocompleteTerm) {
            const altSearchTerm = item.altSearchTerm;
            const parentLabel = item.parentLabel;
            const label = item.label;

            // If it's the parent label and the autocomplete term starts with parent text, unbold it
            if (item.classes?.split(' ').includes(C_AUTOCOMPLETE.GUIDED_COMPLETIONS.PARENT_CLASS) &&
                autocompleteTerm.startsWith(label)) {
                return `<span class="ds1-utd-weight-normal">${label}</span>`;
            }

            if (altSearchTerm && parentLabel) {
                const curChildText = autocompleteTerm?.split(parentLabel)?.slice(1)?.join('');
                if (!curChildText) {
                    return label;
                }

                return this.unboldSearchTextInLabel(curChildText, label);
            }
        },
        // eslint-disable-next-line complexity
        getBoldfacedLabel(item) {
            if (!item) {
                return '';
            }

            const label = item.label;

            if (!label) {
                return '';
            }
            const autocompleteTerm = this.options?.autoCompleteTerm ||
                                     this.searchParams.autoCompleteTerm;
            
            if (!autocompleteTerm || !autocompleteTerm.trim()) {
                return label;
            }

            const gcLabel = this.handleGuidedCompletionsBoldLabel(item, autocompleteTerm);
            if (gcLabel) {
                return gcLabel;
            }

            return this.unboldSearchTextInLabel(autocompleteTerm, label);
        },
        isPersonalized(item) {
            return item.type === 'personalized';
        },
        getItemClasses(item) {
            const returnArray = [ item.classes ];
            if (!item.isHeader) {
                returnArray.push({ active: this.isActive(item) });
            }
            return returnArray;
        },
        getNewTabClasses(item) {
            let classString = item.subClasses || '';
            classString += !this.isPersonalized(item) ? ' ds1-utd-weight-bold' : '';
            return classString;
        }
    }
};
</script>

<style lang="less" scoped>
  @import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
  @import (reference) '~_acaAssets/global/global.less';

  .autocomplete-panel {
    border: @DS1-UTD-GRAY-SINGLE-BORDER;
    box-shadow: @DS1-BOX-SHADOW-POPOUT;
  }

  .autocomplete-ul {
    .reset-ul();
    .ds1-pb-compact();
    background-color: @DS1-UTD-PRIMARY-BG-COLOR;
    border: @DS1-UTD-GRAY-SINGLE-BORDER;
    box-shadow: @DS1-BOX-SHADOW-POPOUT;
  }

  .autocomplete-ul--with-ask-expert {
    border: 0;
    box-shadow: none;
  }

  .autocomplete-li {
    .reset-li();
    position: relative;

    .ac-routed-drug-label {
      display: inline-block;
    }

    a {
      .ds1-ph-2();
      .ds1-utd-size-3();
      .ds1-pv-var(9.5px, 9.5px);
      
      // Click area things (we want entire row to be clickable)
      .ds1-mr-0(); /* stylelint-disable-line order/order */
      .ds1-ml-0();
      width: 100%;
      box-sizing: border-box;
      display: inline-block;
      cursor: pointer;


      &::before {
        font-size: 15px;
        vertical-align: -15%;
        display: inline-block;
        text-decoration: underline;
      }

      // This hack is for IE. https://stackoverflow.com/a/21902566/752916
      &:hover {
        text-decoration: underline;
      }

      &::before,
      &:hover::before {
        text-decoration: none;
      }
    }
  }

  .wkce-icon-search {
    text-align: left;

    &::before {
      font-weight: normal;
      position: absolute;
      line-height: 0;
      left: 16px;
      top: 22.5px;
    }
  }

  .ac-header {
    .ds1-ph-2();
    .ds1-utd-size-1();
    .ds1-utd-weight-600();
    padding-top: 12px;
    padding-bottom: 2px;
    color: @DS1-UTD-SUB-TEXT-COLOR;
    margin-right: 0;
    margin-left: 0;

    &:not(:first-child) {
      .ds1-mt-compact();
      border-top: 1px solid @DS1-UTD-GRAY-BORDER-COLOR;
    }

    &#ac-search-suggestions-header .wkce-icon-search {
      margin-left: 20px;
      
      &::before {
        top: 23.5px;
      }
    }
  }

  .ac-generic {
    .ds1-utd-size-2();
    .ds1-ph-2();
    padding-top: 10px;
    padding-bottom: 2px;
  }

  .ac-italic {
    font-style: italic;
  }

  .route-link {
    .ds1-pl-4();
  }

  .ac-search-suggestion > a {
    .ds1-pl-5();
    color: @WKCE-GRAY-SHADE2;
  }

  .ac-navable:hover,
  .ac-navable.active {
    background-color: @DS1-UTD-AUTOCOMPLETE-ACTIVE-BG-COLOR;
  }

  .ac-drug-section-result.DRUG_INTERACTION > a::before {
    .ds1-utd-pills();
    .ds1-mr-1();
    position: relative;
    top: -2px;
    background-size: 21px 11px;
    width: 21px;
    height: 11px;
  }

  .ac-drug-section-result.DRUG_SUBSECTION {
    .ds1-pl-4();

    > a::before {
      left: calc(@ds1-spacing-multiplier * 4);
      content: '•';
      color: @WKCE-BLACK;
      position: absolute;
      line-height: 0;
      top: 20.5px;
    }
  }

  .ac-guided-completion-suggestion-parent > a {
    .ds1-pl-5();
    color: @WKCE-GRAY-SHADE2;
  }

  .ac-guided-completion-suggestion-sub {
    > a {
      padding-left: 82px;
      color: @WKCE-GRAY-SHADE2;

      &::before {
        left: 64px;
        content: '•';
        color: @WKCE-BLACK;
        position: absolute;
        line-height: 0;
        top: 17.5px;
      }
    }
  }

  .ac-profile-result {
    display: flex;
    align-items: center;

    .glyph {
      .ds1-orange-dot();
      display: none;
      font-size: 12px;
      left: 5.5px;
    }

    &.changed {
      .glyph {
        display: block;
      }
    }

    &[class*="graphic_"] a::before {
      .ds1-mr-1();
      .ds1-utd-image();
      position: relative;
      display: inline-block;
    }

    &.graphic_figure a::before {
      .ds1-utd-figure();
    }

    &.graphic_table a::before {
      .ds1-utd-table();
    }

    &.graphic_movie a::before {
      .ds1-utd-movie();
    }

    &.graphic_algorithm a::before {
      .ds1-utd-algorithm();
    }
  }

  // Specificity shims (remove after searchbar.less is redone)
  .utdWkHeader .utd-search .search-bar-ac-unit .autocomplete ul.utd-listView li.ac-header {
    padding: 12px 16px 6px;
  }

  /* stylelint-disable-next-line order/order */
  @media only screen and (min-width: 768px) {
    .autocomplete-li {
      a {
        .ds1-pv-var(5.5px, 5.5px);
      }
    }

    .ac-drug-section-result.DRUG_SUBSECTION > a::before {
      top: 16.5px;
    }

    .wkce-icon-search::before {
      top: 18.5px;
    }

    .ac-search-suggestion > a {
      .ds1-pl-5();
    }
  }
</style>
